import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Save, ArrowLeft, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import LayoutEditor from "./LayoutEditor";

const slugify = (s) => (s || "")
  .toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "")
  .replace(/\s+/g, "-")
  .replace(/-+/g, "-").replace(/^-|-$/g, "");

export function PagesList() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/admin/pages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/pages", {
        slug: `new-page-${Date.now()}`,
        title: "New page",
        hero: { overline: "", headline: "New page", subheadline: "", image_url: "" },
        layout: [],
      });
      nav(`/admin/pages/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create page");
    } finally { setCreating(false); }
  };

  const remove = async (id, title) => {
    if (!confirm(`Delete "${title}"? This can't be undone.`)) return;
    await api.delete(`/admin/pages/${id}`);
    toast.success("Page deleted");
    load();
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/pages/${id}/duplicate`);
      toast.success("Duplicated as draft");
      nav(`/admin/pages/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not duplicate");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Custom pages</div>
          <h1 className="font-display text-4xl mt-3">Pages</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">Build any page — About, Contact, Careers, seasonal offers — using the same drag‑and‑drop section builder as properties. Toggle a page into the top navigation with one click.</p>
        </div>
        <button onClick={create} disabled={creating} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md hover:bg-black disabled:opacity-60" data-testid="new-page-btn">
          <Plus className="w-4 h-4" /> {creating ? "Creating…" : "New page"}
        </button>
      </div>

      <div className="mt-8 bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Slug</th>
              <th className="p-3 text-left">In menu</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-zinc-500">No pages yet. Click <b>New page</b> to build your first one.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100" data-testid={`page-row-${p.slug}`}>
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3 text-zinc-500">/{p.slug}</td>
                <td className="p-3">{p.show_in_header ? <span className="text-[10px] px-2 py-1 rounded-full bg-sky-100 text-sky-700">Header</span> : <span className="text-[10px] text-zinc-400">—</span>}</td>
                <td className="p-3"><span className={`text-[10px] px-2 py-1 rounded-full ${p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{p.is_published ? "Published" : "Draft"}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Link to={`/admin/pages/${p.id}`} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md mr-2 hover:bg-zinc-50" data-testid={`page-edit-${p.slug}`}>Edit</Link>
                  {p.is_published && <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md inline-flex items-center gap-1 mr-2 hover:bg-zinc-50"><ExternalLink className="w-3 h-3" /></a>}
                  <button onClick={() => duplicate(p.id)} title="Duplicate" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md mr-2 hover:bg-zinc-50 inline-flex items-center gap-1" data-testid={`page-duplicate-${p.slug}`}><Copy className="w-3 h-3" /></button>
                  <button onClick={() => remove(p.id, p.title)} className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md" data-testid={`page-delete-${p.slug}`}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PageEdit() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get(`/admin/pages/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <div>Loading…</div>;

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setHero = (k, v) => setP((x) => ({ ...x, hero: { ...(x.hero || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/pages/${id}`, p);
      setP(data);
      toast.success("Page saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    await api.delete(`/admin/pages/${id}`);
    toast.success("Page deleted");
    nav("/admin/pages");
  };

  return (
    <div>
      <Link to="/admin/pages" className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900"><ArrowLeft className="w-3 h-3" /> All pages</Link>
      <div className="flex items-end justify-between mt-3 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Editing page</div>
          <h1 className="font-display text-3xl mt-2">{p.title || "Untitled"}</h1>
          <div className="text-xs text-zinc-500 mt-1">Public URL: <code className="bg-zinc-100 px-1.5 py-0.5 rounded">/{p.slug}</code></div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="page-published-toggle" /> Published</label>
          {p.is_published && <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-3 py-2 border rounded-md hover:bg-zinc-50"><ExternalLink className="w-3 h-3" /> Preview</a>}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md hover:bg-black disabled:opacity-60" data-testid="page-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Tabs defaultValue="basics" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="basics" data-testid="tab-basics">Basics</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
        </TabsList>

        {/* BASICS */}
        <TabsContent value="basics" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-500">Page title</label>
              <Input value={p.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. About us" data-testid="field-title" />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Slug (URL)</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-500">/</span>
                <Input value={p.slug || ""} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="about-us" data-testid="field-slug" />
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">Lowercase letters, numbers and dashes. Must be unique.</div>
            </div>
          </div>

          <div className="border-t pt-6">
            <div className="text-sm font-medium mb-3">Hero</div>
            <div className="space-y-3">
              <Input value={p.hero?.overline || ""} onChange={(e) => setHero("overline", e.target.value)} placeholder="Overline (small label)" data-testid="field-hero-overline" />
              <Input value={p.hero?.headline || ""} onChange={(e) => setHero("headline", e.target.value)} placeholder="Headline" data-testid="field-hero-headline" />
              <Textarea value={p.hero?.subheadline || ""} onChange={(e) => setHero("subheadline", e.target.value)} placeholder="Subheadline (optional)" rows={3} data-testid="field-hero-sub" />
              <ImageUploader label="Hero background image" value={p.hero?.image_url} onChange={(v) => setHero("image_url", v)} testId="field-hero-image" />
            </div>
          </div>
        </TabsContent>

        {/* SECTIONS */}
        <TabsContent value="sections" className="mt-6">
          <div className="bg-white border border-zinc-200 rounded-md p-4 md:p-6">
            <LayoutEditor
              layout={(p.layout || []).length ? p.layout : []}
              onChange={(next) => set("layout", next)}
              coreEnabled={false}
            />
          </div>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="space-y-4 mt-6">
          <div>
            <label className="text-xs text-zinc-500">Meta title</label>
            <Input value={p.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} placeholder="60 chars max, ideally" data-testid="field-meta-title" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Meta description</label>
            <Textarea value={p.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} placeholder="150–160 chars" rows={3} data-testid="field-meta-desc" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Keywords</label>
            <Input value={p.keywords || ""} onChange={(e) => set("keywords", e.target.value)} placeholder="Comma-separated" data-testid="field-keywords" />
          </div>
          <ImageUploader imageOnly label="Open Graph image (social share preview)" value={p.og_image} onChange={(v) => set("og_image", v)} testId="field-og" />
          <div>
            <label className="text-xs text-zinc-500">Custom schema JSON (optional)</label>
            <Textarea value={p.schema_json || ""} onChange={(e) => set("schema_json", e.target.value)} placeholder='{"@context":"https://schema.org", ...}' rows={5} className="font-mono text-xs" data-testid="field-schema" />
          </div>
        </TabsContent>

        {/* MENU */}
        <TabsContent value="menu" className="space-y-4 mt-6">
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={!!p.show_in_header} onCheckedChange={(v) => set("show_in_header", v)} data-testid="field-show-in-header" />
            <span>Show this page in the top navigation menu</span>
          </label>
          {p.show_in_header && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-1">
              <div>
                <label className="text-xs text-zinc-500">Menu label</label>
                <Input value={p.header_label || ""} onChange={(e) => set("header_label", e.target.value)} placeholder={p.title || "Menu label"} data-testid="field-header-label" />
                <div className="text-[11px] text-zinc-400 mt-1">Defaults to the page title.</div>
              </div>
              <div>
                <label className="text-xs text-zinc-500">Menu order</label>
                <Input type="number" value={p.header_order ?? 999} onChange={(e) => set("header_order", Number(e.target.value) || 0)} placeholder="e.g. 50" data-testid="field-header-order" />
                <div className="text-[11px] text-zinc-400 mt-1">Lower numbers appear first.</div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-10 border-t pt-6 flex justify-between">
        <button onClick={remove} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-2" data-testid="page-delete-bottom"><Trash2 className="w-4 h-4" /> Delete this page</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md hover:bg-black disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
